/**
 * Materiality assessment report: front matter + 12 main sections (flat navigation).
 */

import {
	getMaterialityPageTemplateHtml,
	hasMaterialityPageTemplate,
	prependMaterialitySectionTitle,
} from './materialityReportPageTemplates';

export const FRONT_COVER_PAGE_NAME = 'Front Cover page';
export const TABLE_OF_CONTENTS_PAGE_NAME = 'Table of Contents';

export const REPORT_PAGE_DEFINITIONS = [
	{
		name: FRONT_COVER_PAGE_NAME,
		legacyNames: [FRONT_COVER_PAGE_NAME],
		dynamic: true,
		aiEnabled: false,
		isCover: true,
	},
	{
		name: TABLE_OF_CONTENTS_PAGE_NAME,
		legacyNames: [TABLE_OF_CONTENTS_PAGE_NAME, 'TableofContents'],
		dynamic: true,
		aiEnabled: false,
	},
	{
		name: 'Executive Summary',
		legacyNames: ['Introduction', 'Executive Summary'],
		dynamic: false,
		aiEnabled: true,
	},
	{
		name: 'About Company',
		legacyNames: ['About Company'],
		dynamic: false,
		aiEnabled: true,
	},
	{
		name: 'Assessment Methodology',
		legacyNames: ['Methodology', 'Assessment Methodology'],
		legacyPageNames: ['Overview', 'Understanding', 'Identification', 'Assessment', 'Determination'],
		dynamic: false,
		aiEnabled: true,
		mergeLegacyPages: true,
	},
	{
		name: 'Stakeholder Identification & Engagement',
		legacyNames: ['Stakeholder Identification & Engagement', 'Stakeholder Identification'],
		dynamic: false,
		aiEnabled: true,
	},
	{
		name: 'Peer Benchmarking',
		legacyNames: ['Peer Benchmarking'],
		dynamic: false,
		aiEnabled: true,
	},
	{
		name: 'Material Topics Overview',
		legacyNames: ['Material Topics Overview'],
		dynamic: false,
		aiEnabled: true,
	},
	{
		name: 'Material Topics — Scorecard',
		legacyNames: ['Scorecard', 'Material Topics — Scorecard'],
		dynamic: true,
		aiEnabled: false,
	},
	{
		name: 'Materiality Matrix',
		legacyNames: [
			'Materiality Matrix',
			'Single Materiality Matrix',
			'Single Materiality Mat',
			'Double Materiality Matrix',
		],
		dynamic: true,
		aiEnabled: false,
	},
	{
		name: 'Material Topics — GRI & SDG Mapping',
		legacyNames: ['Material Topics — GRI & SDG Mapping', 'GRI & SDG Mapping'],
		dynamic: true,
		aiEnabled: false,
	},
	{
		name: 'Material Topics — Key Data Points',
		legacyNames: ['Material Topics — Key Data Points', 'Key Data Points'],
		dynamic: true,
		aiEnabled: false,
	},
	{
		name: 'Actions & Recommendations',
		legacyNames: ['Actions & Recommendations', 'Actions & Recommen'],
		dynamic: false,
		aiEnabled: true,
	},
	{
		name: 'Conclusion',
		legacyNames: ['Conclusion'],
		dynamic: false,
		aiEnabled: true,
	},
];

export const AI_ENABLED_PAGE_NAMES = new Set(
	REPORT_PAGE_DEFINITIONS.filter((d) => d.aiEnabled).map((d) => d.name)
);

/** Main report sections listed in the Table of Contents (excludes cover and TOC). */
export const MATERIALITY_TOC_SECTION_NAMES = REPORT_PAGE_DEFINITIONS.filter(
	(def) => def.name !== FRONT_COVER_PAGE_NAME && def.name !== TABLE_OF_CONTENTS_PAGE_NAME
).map((def) => def.name);

const escapeHtml = (text) =>
	String(text)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');

/**
 * Table of Contents HTML for the materiality report (flat numbered list, legacy visual style).
 */
export const buildMaterialityTableOfContentsHtml = () => {
	const listItems = MATERIALITY_TOC_SECTION_NAMES.map(
		(name) =>
			`<li style="margin: 0 0 14px; line-height: 1.45; color: #334155;">
				<strong style="font-weight: 700;">${escapeHtml(name)}</strong>
			</li>`
	).join('');

	return `<div style="font-family: Arial, Helvetica, sans-serif; padding: 48px 56px; color: #334155;">
		<h2 style="font-size: 22pt; font-weight: 700; color: #2dc26b; text-transform: uppercase; letter-spacing: 0.03em; margin: 0 0 40px 0;">
			TABLE OF CONTENTS
		</h2>
		<ol style="margin: 0; padding-left: 28px; font-size: 13pt; list-style-type: decimal;">
			${listItems}
		</ol>
	</div>`;
};

const normalizeName = (name) => (name ?? '').trim().toLowerCase();

const nameMatches = (a, b) => {
	const na = normalizeName(a);
	const nb = normalizeName(b);
	if (!na || !nb) return false;
	return na === nb || na.startsWith(nb) || nb.startsWith(na);
};

const matchesAnyLegacy = (name, legacyNames = []) =>
	legacyNames.some((legacy) => nameMatches(name, legacy));

const TEMPLATE_VERSION_MARKER = 'mat-report-template-v2';

const isHtmlEmpty = (html) => !html || !String(html).replace(/<[^>]*>/g, '').trim();

const shouldApplyPageTemplate = (def, legacyHtml) => {
	if (!hasMaterialityPageTemplate(def.name)) {
		return false;
	}
	if (def.dynamic && !isHtmlEmpty(legacyHtml)) {
		return false;
	}
	if (isHtmlEmpty(legacyHtml)) {
		return true;
	}
	return !String(legacyHtml).includes(TEMPLATE_VERSION_MARKER);
};

const collectLegacyEntries = (sections) => {
	const entries = [];
	sections?.forEach((section) => {
		section?.pages?.forEach((page) => {
			entries.push({
				section,
				page,
				sectionName: section?.name,
				pageName: page?.name,
				html: page?.html ?? null,
			});
		});
		if (!section?.pages?.length && section?.name) {
			entries.push({
				section,
				page: null,
				sectionName: section?.name,
				pageName: section?.name,
				html: null,
			});
		}
	});
	return entries;
};

const findEntryHtml = (entries, def) => {
	const exactSection = entries.find((e) => nameMatches(e.sectionName, def.name));
	if (exactSection?.html && !isHtmlEmpty(exactSection.html)) {
		return { html: exactSection.html, section: exactSection.section, page: exactSection.page };
	}

	const exactPage = entries.find((e) => nameMatches(e.pageName, def.name));
	if (exactPage?.html && !isHtmlEmpty(exactPage.html)) {
		return { html: exactPage.html, section: exactPage.section, page: exactPage.page };
	}

	for (const legacy of def.legacyNames ?? []) {
		const match = entries.find(
			(e) => nameMatches(e.sectionName, legacy) || nameMatches(e.pageName, legacy)
		);
		if (match?.html && !isHtmlEmpty(match.html)) {
			return { html: match.html, section: match.section, page: match.page };
		}
	}

	if (def.mergeLegacyPages) {
		const merged = [];
		let sectionRef = null;
		let pageRef = null;
		const legacyPageNames = [
			...(def.legacyNames ?? []),
			...(def.legacyPageNames ?? []),
		];
		const methodologyEntries = entries
			.filter(
				(e) =>
					nameMatches(e.sectionName, 'Methodology') ||
					legacyPageNames.some(
						(n) => nameMatches(e.sectionName, n) || nameMatches(e.pageName, n)
					)
			)
			.sort((a, b) => (a.page?.sortOrder ?? 0) - (b.page?.sortOrder ?? 0));
		methodologyEntries.forEach((e) => {
			if (e.html && !isHtmlEmpty(e.html)) {
				merged.push(e.html);
				sectionRef = sectionRef ?? e.section;
				pageRef = pageRef ?? e.page;
			}
		});
		if (merged.length) {
			return {
				html: merged.join('<br/><br/>'),
				section: sectionRef,
				page: pageRef,
			};
		}
	}

	return { html: null, section: null, page: null };
};

/**
 * Normalize API report sections to front matter + 12 flat main pages for sidebar and editing.
 */
export const normalizeReportSections = (reportData) => {
	if (!reportData || typeof reportData !== 'object') {
		return reportData;
	}

	const legacyEntries = collectLegacyEntries(reportData.sections);
	let pageNum = 0;

	const sections = REPORT_PAGE_DEFINITIONS.map((def, sindex) => {
		const { html: legacyHtml, section: legacySection, page: legacyPage } = findEntryHtml(
			legacyEntries,
			def
		);

		let html = legacyHtml;
		if (def.name === TABLE_OF_CONTENTS_PAGE_NAME) {
			html = buildMaterialityTableOfContentsHtml();
		} else if (shouldApplyPageTemplate(def, legacyHtml)) {
			html = getMaterialityPageTemplateHtml(def.name);
		}

		if (html && hasMaterialityPageTemplate(def.name)) {
			html = prependMaterialitySectionTitle(def.name, html);
		}

		const page = {
			...(legacyPage?.id != null ? { id: legacyPage.id } : {}),
			name: def.name,
			index: 0,
			sortOrder: 1,
			type: legacyPage?.type ?? legacySection?.type,
			html: html ?? null,
		};

		// Page numbers start after Table of Contents (same as legacy report)
		if (sindex > 1 && html) {
			page.pageNum = ++pageNum;
		}

		return {
			...(legacySection?.id != null ? { id: legacySection.id } : {}),
			name: def.name,
			index: sindex,
			sortOrder: sindex + 1,
			type: legacySection?.type,
			pages: [page],
		};
	});

	return { ...reportData, sections };
};

export const isAiEnabledPage = (pageName) => AI_ENABLED_PAGE_NAMES.has(pageName);

export const isCoverPage = (pageName) => pageName === FRONT_COVER_PAGE_NAME;
